import { createSelector } from 'reselect';
import { every, indexBy, isNull, isUndefined, mapObject } from 'underscore';

import {
  YOUTUBE_EMAIL_PATTERNS,
  YOUTUBE_ENABLED_FOR_INTEGRATORS,
  YOUTUBE_USER_WHITELIST,
} from 'config';
import {
  PodcastEpisode,
  PodcastSubscription,
} from 'redux/middleware/api/podcast-service/types';
import {
  EmailVerificationStatus,
  IProfile,
} from 'redux/middleware/api/user-service/types';
import { State } from 'redux/types';
import {
  AutogramClipSelectionMethod,
  ImageOriginId,
  RequestStatus,
  SocialPostCaptionInfoPlatform,
  SocialPostCaptions,
  SocialSharePlatform,
  VideoTypes,
} from 'types';
import { getAspectRatioName } from 'utils/aspect-ratio';
import { getValue } from 'utils/collections';
import { DEFAULT_PROJECT_NAME, HEADLINER_SHOUTOUT } from 'utils/constants';
import {
  getBlurRadiusFromConfig,
  getBorderRadiusFromConfig,
  hasSlideFromOrigin,
} from 'utils/embed/embed';
import {
  loggedInSelector,
  loggedOutSelector,
  userIdSelector,
  userIntegratorsSelector,
  userNameSelector,
} from '../auth/selectors';
import { shareMethodOverrideSelector } from '../display-pref';
import {
  audioVariationsSelector,
  autoCreateEpisodesSelector,
  autoCreateEpisodeVideosSelector,
  displayPrefsSelector,
  embedExportsSelector,
  embedVideoSocialPostCaptionsSelector,
  podcastFeedEpisodesSelector,
  podcastSubscriptionsSelector,
  projectsDetailsSelector,
  userProfilesSelector,
  videoSocialPostCaptionsSelector,
} from '../entities';
import { isFreeSelector } from '../pricing/selectors';
import { getProjectTypeByProject, getTemplateId } from '../project/utils';
import { DownloadState } from './types';

const DEFAULT_VIDEO_NAME = `${DEFAULT_PROJECT_NAME}.mp4`;

const stateSelector = (state: State): DownloadState => state.get('download');

export const loadingStateSelector = createSelector(
  stateSelector,
  state => state.loadingState,
);

export const isLoadingSelector = createSelector(
  loadingStateSelector,
  loadingState => loadingState === RequestStatus.REQUEST,
);

const configurationSelector = createSelector(
  stateSelector,
  state => state.configuration,
);

export const projectIdSelector = createSelector(
  stateSelector,
  state => state.projectId,
);

export const podcastInfoSelector = createSelector(stateSelector, state =>
  state.podcastInfo?.toJS(),
);

export const projectSelector = createSelector(
  [projectsDetailsSelector, projectIdSelector],
  (projects, projectId) => {
    if (!projects || !projectId) return undefined;
    return projects.get(projectId);
  },
);

export const projectByIdSelector = (projectId: string, state) => {
  return projectsDetailsSelector(state)?.get(projectId);
};

export const configurationIdSelector = createSelector(
  configurationSelector,
  config => config && config.get('wid'),
);

const exportSelector = createSelector(
  [embedExportsSelector, configurationIdSelector],
  (embedExports, configId) => {
    if (!embedExports || !configId) return undefined;
    return embedExports.get(configId);
  },
);

export const templateIdSelector = createSelector(projectSelector, project =>
  getTemplateId(project),
);

export const subscriptionSelector = createSelector(
  projectSelector,
  podcastSubscriptionsSelector,
  (project, subscriptions) => {
    const subscriptionId = project?.getIn([
      'automationSubscriptionSource',
      'subId',
    ]);
    return subscriptions
      ?.get(String(subscriptionId))
      ?.toJS() as PodcastSubscription;
  },
);

export const subscriptionItemSelector = createSelector(
  projectSelector,
  subscriptionSelector,
  (project, subscription) => {
    const subscriptionItemId = project?.getIn([
      'automationSubscriptionSource',
      'subItemId',
    ]);
    return subscription?.subscriptionItems?.find(
      item => item.id === subscriptionItemId,
    );
  },
);

export const projectTypeSelector = createSelector(projectSelector, project => {
  if (!project) return undefined;
  return getProjectTypeByProject(project);
});

export const queuerUserIdSelector = createSelector(
  exportSelector,
  embedExport => embedExport?.get('queuerUserId'),
);

export const projectOwnerIdSelector = createSelector(
  projectSelector,
  queuerUserIdSelector,
  (project, queuerUserId) => {
    return project?.get('creatorUserId') ?? queuerUserId;
  },
);

export const queuerUserProfileSelector = createSelector(
  queuerUserIdSelector,
  userProfilesSelector,
  (queuerUserId, profiles): IProfile => {
    return profiles?.get(String(queuerUserId))?.toJS();
  },
);

export const isPodcastAutomationSelector = createSelector(
  exportSelector,
  embedExport => {
    return embedExport?.get('isPodcastAutomation');
  },
);

export const initiatorInfoSelector = createSelector(
  exportSelector,
  embedExport => {
    return embedExport?.get('initiatorInfo')?.toJS();
  },
);

export const creationRequestIdSelector = createSelector(
  initiatorInfoSelector,
  initiatorInfo => initiatorInfo?.creationRequestId,
);

export const isVideoTiedToSuggestionGroupSelector = createSelector(
  initiatorInfoSelector,
  ({ creationRequestId, creationSuggestionId }) => {
    return Boolean(creationRequestId && creationSuggestionId);
  },
);

export const isQueuerUserVerifiedSelector = createSelector(
  queuerUserProfileSelector,
  profile => {
    return (
      profile?.emailVerification?.emailVerificationStatus ===
      EmailVerificationStatus.VERIFIED
    );
  },
);

export const automationSubIdSelector = createSelector(
  projectSelector,
  project => {
    return project?.getIn(['automationSubscriptionSource', 'subId']);
  },
);

export const videoUrlSelector = createSelector(exportSelector, embedExport => {
  if (!embedExport) return undefined;
  return embedExport.get('videoUrl');
});

export const videoIdSelector = createSelector(exportSelector, embedExport =>
  embedExport?.get('id'),
);

export const aspectRatioNameSelector = createSelector(
  configurationSelector,
  config => {
    const dimensions = getValue(config, ['embedConfig', 'dimensions']);
    if (!dimensions) return undefined;

    const height = dimensions.get('height');
    const width = dimensions.get('width');

    return getAspectRatioName(height, width);
  },
);

export const waveformPrefIdSelector = createSelector(
  configurationSelector,
  config => getValue(config, ['embedConfig', 'soundwave', 'waveformPrefId']),
);

export const projectNameSelector = createSelector(projectSelector, project => {
  if (!project) return DEFAULT_PROJECT_NAME;

  return getValue(
    project,
    ['projectConfig', 'projectName'],
    DEFAULT_PROJECT_NAME,
  );
});

export const videoFileNameSelector = createSelector(
  [projectSelector, projectNameSelector, isFreeSelector, loggedOutSelector],
  (project, projectName, isFree, loggedOut) => {
    if (!project) return DEFAULT_VIDEO_NAME;

    if (isFree || loggedOut) {
      return `${projectName} (Made by Headliner).mp4`;
    }
    return `${projectName}.mp4`;
  },
);

export const eddyProjectUrlSelector = createSelector(
  projectSelector,
  project => {
    if (!project) return undefined;

    return getValue(project, ['projectConfig', 'eddyProjectUrl']);
  },
);

export const episodeIdSelector = createSelector(projectSelector, project => {
  if (!project) return undefined;
  return project.getIn(['automationSubscriptionSource', 'episodeId']);
});

export const subscriptionItemIdSelector = createSelector(
  projectSelector,
  project => {
    if (!project) return undefined;
    return project.getIn(['automationSubscriptionSource', 'subItemId']);
  },
);

export const subscriptionIdSelector = createSelector(
  projectSelector,
  project => {
    if (!project) return undefined;
    return project.getIn(['automationSubscriptionSource', 'subId']);
  },
);

export const podcastEpisodeIdSelector = createSelector(
  projectSelector,
  project => {
    if (!project) return undefined;
    return project.getIn(['podcastSource', 'podcastEpisodeId']);
  },
);

export const audioVariationTypeSelector = createSelector(
  videoIdSelector,
  autoCreateEpisodeVideosSelector,
  autoCreateEpisodesSelector,
  audioVariationsSelector,
  (videoId, videos, episodes, audioVariations) => {
    const autoCreateEpisodeId = videos
      ?.get(String(videoId))
      ?.get('autoCreateEpisode');
    const autoCreateEpisode = episodes?.get(String(autoCreateEpisodeId));
    const variation = audioVariations?.get(
      String(autoCreateEpisode?.get('audioVariationId')),
    );
    return variation?.get('audioVariationType');
  },
);

export const autoCreateEpisodeSelector = createSelector(
  videoIdSelector,
  autoCreateEpisodeVideosSelector,
  autoCreateEpisodesSelector,
  (videoId, videos, episodes) => {
    const autoCreateEpisodeId = videos
      ?.get(String(videoId))
      ?.get('autoCreateEpisode');
    const autoCreateEpisode = episodes?.get(String(autoCreateEpisodeId));

    return autoCreateEpisode;
  },
);

export const isAutoCreateEpisodeSelector = createSelector(
  autoCreateEpisodeSelector,
  autoCreateEpisode => !!autoCreateEpisode,
);

export const clipSelectionMethodSelector = createSelector(
  subscriptionItemSelector,
  subscription => subscription?.clipSelectionMethod,
);

export const canReclipSelector = createSelector(
  [userIdSelector, podcastSubscriptionsSelector],
  (userId, subscription) => {
    const subscriptionOwnerId = subscription?.first()?.get('userId');
    const subscriptionItems = subscription
      ?.first()
      .get('subscriptionItems')
      ?.first();
    const videoType = subscriptionItems?.get('videoType');
    const clipSelectionMethod = subscriptionItems?.get('clipSelectionMethod');

    return (
      userId &&
      subscriptionOwnerId &&
      userId === subscriptionOwnerId &&
      videoType === VideoTypes.RANDOM_CLIP &&
      clipSelectionMethod === AutogramClipSelectionMethod.SMART
    );
  },
);

export const recordingIdSelector = createSelector(
  configurationSelector,
  config => getValue(config, ['embedConfig', 'recordingId']),
);

export const createMethodSelector = createSelector(projectSelector, project =>
  getValue(project, 'createMethod'),
);

export const videoFrequencySelector = createSelector(
  stateSelector,
  state => state.videoFrequency,
);

export const videoDurationMillisSelector = createSelector(
  stateSelector,
  state => state.videoDurationMillis,
);

export const videoDurationSecondsSelector = createSelector(
  videoDurationMillisSelector,
  videoDurationMillis => videoDurationMillis / 1000,
);

export const isYouTubeEnabledSelector = createSelector(
  [userNameSelector, userIdSelector, userIntegratorsSelector],
  (userName, userId, userIntegrators) => {
    const omnyIntegrator = userIntegrators.find(integrator =>
      YOUTUBE_ENABLED_FOR_INTEGRATORS.includes(
        integrator.integratorIdentityProviderName,
      ),
    );

    return (
      !isUndefined(omnyIntegrator) ||
      YOUTUBE_USER_WHITELIST.some(
        whitelistUserId => whitelistUserId && userId === whitelistUserId,
      ) ||
      YOUTUBE_EMAIL_PATTERNS.some(pat => pat && RegExp(pat).test(userName))
    );
  },
);

export const podcastEpisodeSelector = createSelector(
  [podcastFeedEpisodesSelector, podcastEpisodeIdSelector],
  (podcastEpisodes, episodeId) =>
    podcastEpisodes?.get(String(episodeId))?.toJS() as PodcastEpisode,
);

export const isPollingSocialPostCaptionsSelector = createSelector(
  stateSelector,
  state => state?.isPollingSocialPostCaptions,
);

export const isErrorSocialPostCaptionsSelector = createSelector(
  stateSelector,
  state => state?.hasError,
);

export const socialPostCaptionInfoSelector = createSelector(
  podcastEpisodeSelector,
  episode => episode?.socialPostCaptionInfo,
);

export const canGenerateSocialPostCaptionsSelector = createSelector(
  socialPostCaptionInfoSelector,
  socialPostCaptionInfo => socialPostCaptionInfo?.canGenerateSocialPostCaptions,
);

export const socialPostCaptionInfoByPlatformSelector = createSelector(
  socialPostCaptionInfoSelector,
  socialPostCaptionInfo => {
    if (socialPostCaptionInfo) {
      return mapObject(
        indexBy(socialPostCaptionInfo?.socialPostCaptions, 'platform'),
        platform => platform,
      ) as Record<SocialSharePlatform, SocialPostCaptions>;
    }
    return undefined;
  },
);

export const hasSocialPostCaptionInfoSelector = createSelector(
  socialPostCaptionInfoSelector,
  socialPostCaptionInfo =>
    !every(socialPostCaptionInfo?.socialPostCaptions, isNull),
);

export const episodeUrlSelector = createSelector(
  subscriptionSelector,
  podcastEpisodeSelector,
  podcastInfoSelector,
  (subscription, episode, podcastInfo) => {
    let episodeUrl = '';

    if (subscription) {
      // As subscription videos don't bring episodeInfo, the url has to be obtained from episode
      episodeUrl = episode?.playAppEpisodeUrl || episode?.url;
    } else {
      // For autograms episode url will be obtained from podcastInfo
      episodeUrl = podcastInfo?.episode.playAppUrl || podcastInfo?.episode.url;
    }

    return episodeUrl;
  },
);

/**
 * returns the title of the podcast that was used to create this project.
 */
export const projectPodcastNameSelector = createSelector(
  [podcastInfoSelector],
  podcastInfo => podcastInfo?.podcast?.title,
);

export const defaultPostTextSelector = createSelector(
  projectPodcastNameSelector,
  episodeUrlSelector,
  isFreeSelector,
  loggedOutSelector,
  (podcastName, episodeUrl, isFree, loggedOut) => {
    let post = '';

    if (podcastName) {
      post += `Check out the latest episode of ${podcastName}!`;
    } else {
      post += 'Check out my new video!';
    }

    if (episodeUrl) {
      post += ` Listen to the full podcast here ${episodeUrl}`;
    }

    if (isFree || loggedOut) {
      return `${post} ${HEADLINER_SHOUTOUT}`;
    }

    return post;
  },
);

/**
 * Returns the post text to use for social share: default post text when free,
 * logged out, or when there is no platform caption; otherwise the platform
 * caption.
 */
export const socialSharePostTextSelector = (
  platform: SocialSharePlatform | undefined,
  state: State,
): string => {
  const defaultPostText = defaultPostTextSelector(state);
  const {
    socialPostCaptionInfoByPlatform,
    canGenerateSocialPostCaptions,
  } = selectedSocialPostCaptionsConfigSelector(state);
  const isFree = isFreeSelector(state);
  const isAuthenticated = loggedInSelector(state);
  const platformCaption = socialPostCaptionInfoByPlatform?.[platform]?.postText;

  const useDefaultPostText =
    isFree ||
    !isAuthenticated ||
    !platformCaption ||
    !canGenerateSocialPostCaptions;

  return useDefaultPostText ? defaultPostText : platformCaption;
};

export const captionsEnabledSelector = createSelector(
  configurationSelector,
  config => {
    const captionsEnabled = getValue(config, [
      'embedConfig',
      'captions',
      'enabled',
    ]);
    return captionsEnabled;
  },
);

export const hasSlideFromOriginSelector = (origin: ImageOriginId, state) => {
  return hasSlideFromOrigin(origin, configurationSelector(state));
};

export const blurRadiusSelector = state => {
  const config = configurationSelector(state)
    ?.get('embedConfig')
    ?.toJS();
  return getBlurRadiusFromConfig(config);
};

export const borderRadiusSelector = state => {
  const config = configurationSelector(state)
    ?.get('embedConfig')
    ?.toJS();
  return getBorderRadiusFromConfig(config);
};

export const shareMethodOverridesSelector = createSelector(
  displayPrefsSelector,
  projectOwnerIdSelector,
  shareMethodOverrideSelector,
  (displayPrefs, projectOwnerId, shareMethodOverride) => {
    const projectOwnerDisplayPref = displayPrefs?.get(String(projectOwnerId));

    const ownerShareMethodOverrides = projectOwnerDisplayPref
      ?.getIn([
        'displayAdjustment',
        'videoShare',
        'shareMethod',
        'overrideOptions',
      ])
      ?.toJS();

    return ownerShareMethodOverrides ?? shareMethodOverride;
  },
);

export const dpaPerformedSelector = createSelector(stateSelector, state =>
  state.get('dpaPerformed'),
);

export const videoSocialPostCaptionsJobIdSelector = createSelector(
  stateSelector,
  state => state.get('jobId'),
);

export const videoSocialPostCaptionsDetailSelector = createSelector(
  videoSocialPostCaptionsSelector,
  videoSocialPostCaptionsJobIdSelector,
  (captions, jobId) => captions?.get(String(jobId))?.toJS(),
);

export const canGenerateVideoSocialPostCaptionsSelector = createSelector(
  videoSocialPostCaptionsDetailSelector,
  info => info?.canGenerateSocialPostCaptions,
);

export const videoSocialPostCaptionsStatusSelector = createSelector(
  videoSocialPostCaptionsDetailSelector,
  info => info?.status,
);

export const hasVideoSocialPostCaptionsSelector = createSelector(
  videoSocialPostCaptionsDetailSelector,
  videoSocialPostCaptionsStatusSelector,
  status => status === 'completed',
);

export const videoSocialPostCaptionInfoSelector = createSelector(
  videoSocialPostCaptionsDetailSelector,
  info => info?.socialPostCaptions,
);

export const hasVideoSocialPostCaptionInfoSelector = createSelector(
  videoSocialPostCaptionInfoSelector,
  captions => {
    if (captions) return !every(captions, isNull);
    return undefined;
  },
);

export const videoSocialPostCaptionInfoByPlatformSelector = createSelector(
  videoSocialPostCaptionInfoSelector,
  socialPostCaptionInfo => {
    if (socialPostCaptionInfo)
      return mapObject(
        indexBy(socialPostCaptionInfo, 'platform'),
        platform => platform,
      ) as Record<SocialPostCaptionInfoPlatform, SocialPostCaptions>;
    return undefined;
  },
);

export const embedVideoSocialPostCaptionsJobIdSelector = createSelector(
  stateSelector,
  state => state.get('jobId'),
);

export const embedVideoSocialPostCaptionsDetailSelector = createSelector(
  embedVideoSocialPostCaptionsSelector,
  embedVideoSocialPostCaptionsJobIdSelector,
  (captions, jobId) => captions?.get(String(jobId))?.toJS(),
);

export const embedVideoSocialPostCaptionInfoSelector = createSelector(
  embedVideoSocialPostCaptionsDetailSelector,
  info => info?.socialPostCaptions,
);

export const embedVideoSocialPostCaptionInfoByPlatformSelector = createSelector(
  embedVideoSocialPostCaptionInfoSelector,
  socialPostCaptionInfo => {
    if (socialPostCaptionInfo) {
      return mapObject(
        indexBy(socialPostCaptionInfo, 'platform'),
        platform => platform,
      ) as Record<SocialPostCaptionInfoPlatform, SocialPostCaptions>;
    }

    return undefined;
  },
);

export const hasEmbedVideoSocialPostCaptionInfoSelector = createSelector(
  embedVideoSocialPostCaptionInfoSelector,
  captions => {
    if (captions) return !every(captions, isNull);

    return undefined;
  },
);

export const canGenerateEmbedVideoSocialPostCaptionsSelector = createSelector(
  embedVideoSocialPostCaptionsDetailSelector,
  info => info?.canGenerateSocialPostCaptions,
);

export const embedVideoSocialPostCaptionsStatusSelector = createSelector(
  embedVideoSocialPostCaptionsDetailSelector,
  info => info?.status,
);

/** Source used for selected social post captions (priority: embed > audio > video). */
export type SocialPostCaptionsSource = 'embed' | 'audio' | 'video';

export const embedSocialPostCaptionsConfigSelector = createSelector(
  [
    embedVideoSocialPostCaptionInfoByPlatformSelector,
    hasEmbedVideoSocialPostCaptionInfoSelector,
    canGenerateEmbedVideoSocialPostCaptionsSelector,
  ],
  (
    socialPostCaptionInfoByPlatform,
    hasSocialPostCaptions,
    canGenerateSocialPostCaptions,
  ) => ({
    canGenerateSocialPostCaptions,
    hasSocialPostCaptions,
    socialPostCaptionInfoByPlatform,
  }),
);

export const audioSocialPostCaptionsConfigSelector = createSelector(
  [
    socialPostCaptionInfoByPlatformSelector,
    hasSocialPostCaptionInfoSelector,
    canGenerateSocialPostCaptionsSelector,
  ],
  (
    socialPostCaptionInfoByPlatform,
    hasSocialPostCaptions,
    canGenerateSocialPostCaptions,
  ) => ({
    canGenerateSocialPostCaptions,
    hasSocialPostCaptions,
    socialPostCaptionInfoByPlatform,
  }),
);

export const videoSocialPostCaptionsConfigSelector = createSelector(
  [
    videoSocialPostCaptionInfoByPlatformSelector,
    hasVideoSocialPostCaptionInfoSelector,
    canGenerateVideoSocialPostCaptionsSelector,
  ],
  (
    socialPostCaptionInfoByPlatform,
    hasSocialPostCaptions,
    canGenerateSocialPostCaptions,
  ) => ({
    canGenerateSocialPostCaptions,
    hasSocialPostCaptions,
    socialPostCaptionInfoByPlatform,
  }),
);

/**
 * Selected social post captions config by priority:
 * 1) embed if it has caption data;
 * 2) else audio if episodeId exists;
 * 3) else video if creationRequestId exists;
 * 4) else embed (default).
 */
export const selectedSocialPostCaptionsConfigSelector = createSelector(
  [
    creationRequestIdSelector,
    podcastEpisodeIdSelector,
    embedSocialPostCaptionsConfigSelector,
    audioSocialPostCaptionsConfigSelector,
    videoSocialPostCaptionsConfigSelector,
  ],
  (creationRequestId, episodeId, embedConfig, audioConfig, videoConfig) => {
    if (embedConfig.hasSocialPostCaptions) {
      return { ...embedConfig, source: 'embed' as SocialPostCaptionsSource };
    }

    if (episodeId) {
      return { ...audioConfig, source: 'audio' as SocialPostCaptionsSource };
    }

    if (creationRequestId) {
      return { ...videoConfig, source: 'video' as SocialPostCaptionsSource };
    }

    return { ...embedConfig, source: 'embed' as SocialPostCaptionsSource };
  },
);

export const hasWatermarkSelector = createSelector(
  configurationSelector,
  config => Boolean(config?.getIn(['embedConfig', 'watermark'])?.size),
);
